import { useEffect, useState, type FormEvent } from 'react';
import { Alert, AlertDescription, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, toast } from '@ludolio/shared-ui';
import { Archive, Loader2, Mail, RotateCcw, Save, Trash2, UserPlus, Users } from 'lucide-react';
import { useUnitsModule } from '../../module-context';
import type { AddStaffResult, Semester, Unit, UnitsUser } from '../../types';

interface UnitSettingsTabProps {
  unit: Unit;
  teachers: UnitsUser[];
  onDataChanged(): Promise<void>;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { response?: { data?: { message?: unknown; error?: unknown } }; message?: unknown };
    if (typeof candidate.response?.data?.message === 'string') return candidate.response.data.message;
    if (typeof candidate.response?.data?.error === 'string') return candidate.response.data.error;
    if (typeof candidate.message === 'string') return candidate.message;
  }
  return fallback;
}

function getSemesterLabel(semester: string) {
  const labels: Record<string, string> = {
    semester_1: 'Semester 1',
    semester_2: 'Semester 2',
    summer: 'Summer',
    winter: 'Winter',
  };
  return labels[semester] || semester;
}

function emptyAddStaffResult(): AddStaffResult {
  return {
    added: [],
    pending: [],
    alreadyPending: [],
    notFound: [],
    alreadyAssigned: [],
    notTeacher: [],
  };
}

export function UnitSettingsTab({ unit, teachers, onDataChanged }: UnitSettingsTabProps) {
  const { api, capabilities, currentUser, navigateToUnits, reportError } = useUnitsModule();
  const [metadata, setMetadata] = useState({ code: unit.code, name: unit.name, year: unit.year, semester: unit.semester });
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [updatingArchive, setUpdatingArchive] = useState(false);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [staffEmails, setStaffEmails] = useState('');
  const [addStaffResult, setAddStaffResult] = useState<AddStaffResult | null>(null);
  const [addingStaff, setAddingStaff] = useState(false);
  const [staffToRemove, setStaffToRemove] = useState<string | null>(null);
  const [pendingToRemove, setPendingToRemove] = useState<string | null>(null);
  const [resendingPendingId, setResendingPendingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Unit | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setMetadata({ code: unit.code, name: unit.name, year: unit.year, semester: unit.semester });
  }, [unit]);

  const assignedTeachers = teachers.filter((teacher) => teacher.status !== 'pending');
  const pendingTeachers = teachers.filter((teacher) => teacher.status === 'pending');

  const updateMetadata = async (event: FormEvent) => {
    event.preventDefault();
    if (!capabilities.canEditMetadata || !api.updateUnit) return;
    setSavingMetadata(true);
    try {
      const response = await api.updateUnit(unit.id, {
        ...metadata,
        code: metadata.code.trim(),
        name: metadata.name.trim(),
      });
      if (!response.success) throw new Error(response.error || response.message || 'Failed to update unit');
      await onDataChanged();
      toast.success('Unit details updated');
    } catch (error) {
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to update unit'));
    } finally {
      setSavingMetadata(false);
    }
  };

  const updateArchive = async () => {
    if (!capabilities.canArchiveRestore || !api.updateUnit) return;
    setUpdatingArchive(true);
    try {
      const nextArchived = !unit.isArchived;
      const response = await api.updateUnit(unit.id, { isArchived: nextArchived });
      if (!response.success) throw new Error(response.error || response.message || 'Failed to update unit status');
      await onDataChanged();
      toast.success(nextArchived ? 'Unit archived' : 'Unit restored');
    } catch (error) {
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to update unit status'));
    } finally {
      setUpdatingArchive(false);
    }
  };

  const addStaff = async (event: FormEvent) => {
    event.preventDefault();
    if (!capabilities.canManageAssignedStaff || addingStaff) return;
    const emails = staffEmails.split(/[\n,]/).map((email) => email.trim()).filter(Boolean);
    if (!emails.length) {
      toast.error('Enter at least one email');
      return;
    }
    setAddingStaff(true);
    try {
      const response = await api.addTeachersByEmail(unit.id, emails);
      if (!response.success || !response.data) {
        throw new Error(response.error || response.message || 'Failed to add teachers');
      }
      setAddStaffResult({
        ...emptyAddStaffResult(),
        ...response.data,
        pending: response.data.pending ?? [],
        alreadyPending: response.data.alreadyPending ?? [],
      });
      if (response.data.added.length || (response.data.pending?.length ?? 0) > 0) {
        await onDataChanged();
      }
    } catch (error) {
      console.error('Failed to add teachers:', error);
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to add teachers'));
    } finally {
      setAddingStaff(false);
    }
  };

  const removeStaff = async () => {
    if (!staffToRemove || !capabilities.canManageAssignedStaff || staffToRemove === currentUser?.id) return;
    try {
      await api.removeTeacherFromUnit(unit.id, staffToRemove);
      setStaffToRemove(null);
      await onDataChanged();
      toast.success('Teacher removed');
    } catch (error) {
      console.error('Failed to remove teacher:', error);
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to remove teacher'));
    }
  };

  const removePending = async () => {
    if (!pendingToRemove || !capabilities.canManageAssignedStaff || !api.removePendingUnitTeacher) return;
    try {
      const response = await api.removePendingUnitTeacher(unit.id, pendingToRemove);
      if (!response.success) throw new Error(response.error || response.message || 'Failed to remove invite');
      setPendingToRemove(null);
      await onDataChanged();
      toast.success('Invite removed');
    } catch (error) {
      console.error('Failed to remove pending teacher invite:', error);
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to remove invite'));
    }
  };

  const resendPending = async (pendingId: string) => {
    if (!capabilities.canManageAssignedStaff || !api.resendPendingUnitTeacherInvitation) return;
    setResendingPendingId(pendingId);
    try {
      const response = await api.resendPendingUnitTeacherInvitation(unit.id, pendingId);
      if (!response.success) throw new Error(response.error || response.message || 'Failed to resend invite');
      toast.success('Invitation resent');
    } catch (error) {
      console.error('Failed to resend teacher invite:', error);
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to resend invite'));
    } finally {
      setResendingPendingId(null);
    }
  };

  const openDelete = () => {
    if (!capabilities.canPermanentlyDelete) return;
    setDeleteConfirmation('');
    setDeleteTarget(unit);
  };

  const deleteUnit = async () => {
    if (!capabilities.canPermanentlyDelete || deleting || !deleteTarget || deleteConfirmation !== deleteTarget.code) return;
    const target = deleteTarget;
    setDeleting(true);
    try {
      const response = await api.deleteUnit(target.id, deleteConfirmation);
      if (!response.success) throw new Error(response.error || response.message || 'Failed to delete unit');
      const stored = localStorage.getItem('pinnedUnits');
      if (stored) {
        try {
          const pinnedUnits: Unit[] = JSON.parse(stored);
          localStorage.setItem('pinnedUnits', JSON.stringify(pinnedUnits.filter((pinnedUnit) => pinnedUnit.id !== target.id)));
          window.dispatchEvent(new Event('storage'));
        } catch (storageError) {
          console.warn('Failed to update pinned units:', storageError);
        }
      }
      setDeleteTarget(null);
      toast.success(`${target.code} deleted`);
      navigateToUnits();
    } catch (error) {
      console.error('Failed to delete unit:', error);
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to delete unit'));
    } finally {
      setDeleting(false);
    }
  };

  const closeAddStaff = () => {
    setAddStaffOpen(false);
    setStaffEmails('');
    setAddStaffResult(null);
  };

  return (
    <>
      {capabilities.canEditMetadata && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Unit details</CardTitle>
            <CardDescription>Update the unit information shown across the organization.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={updateMetadata} className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="unit-code">Unit code</Label>
                <Input id="unit-code" value={metadata.code} onChange={(event) => setMetadata((current) => ({ ...current, code: event.target.value }))} minLength={2} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit-name">Unit name</Label>
                <Input id="unit-name" value={metadata.name} onChange={(event) => setMetadata((current) => ({ ...current, name: event.target.value }))} minLength={2} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit-year">Year</Label>
                <Input id="unit-year" type="number" min="2000" max="2100" value={metadata.year} onChange={(event) => setMetadata((current) => ({ ...current, year: Number(event.target.value) }))} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit-semester">Semester</Label>
                <Select value={metadata.semester} onValueChange={(value) => setMetadata((current) => ({ ...current, semester: value as Semester }))}>
                  <SelectTrigger id="unit-semester"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semester_1">Semester 1</SelectItem>
                    <SelectItem value="semester_2">Semester 2</SelectItem>
                    <SelectItem value="summer">Summer</SelectItem>
                    <SelectItem value="winter">Winter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end sm:col-span-2">
                <Button type="submit" disabled={savingMetadata || !metadata.code.trim() || !metadata.name.trim()}>
                  {savingMetadata ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save changes
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {capabilities.canArchiveRestore && (
        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-lg">{unit.isArchived ? 'Restore unit' : 'Archive unit'}</CardTitle>
              <CardDescription>{unit.isArchived ? 'Return this unit to active status.' : 'Keep the unit and its data while marking it as archived.'}</CardDescription>
            </div>
            <Button variant="outline" className="self-start sm:self-auto" disabled={updatingArchive} onClick={() => void updateArchive()}>
              {updatingArchive ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : unit.isArchived ? <RotateCcw className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
              {unit.isArchived ? 'Restore unit' : 'Archive unit'}
            </Button>
          </CardHeader>
        </Card>
      )}

      {(capabilities.showAssignedStaff || capabilities.canManageAssignedStaff) && (
        <Card>
          <CardHeader>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-lg">{capabilities.showAssignedStaff ? 'Assigned staff' : 'Unit Teachers'}</CardTitle>
                <CardDescription>
                  {capabilities.showAssignedStaff
                    ? 'Instructors and librarians assigned to this unit.'
                    : 'Teachers assigned to this unit can manage students and games. Invite tutors by email if they do not have an account yet.'}
                </CardDescription>
              </div>
              {capabilities.canManageAssignedStaff && (
                <Dialog open={addStaffOpen} onOpenChange={(open) => {
                  setAddStaffOpen(open);
                  if (!open) {
                    setStaffEmails('');
                    setAddStaffResult(null);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button><UserPlus className="mr-2 h-4 w-4" />{capabilities.showAssignedStaff ? 'Add staff' : 'Add Teachers'}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{capabilities.showAssignedStaff ? 'Add staff by email' : 'Add Teachers by Email'}</DialogTitle>
                      <DialogDescription>
                        Enter email addresses (one per line or comma-separated). Existing instructors are added immediately. New emails receive an instructor invitation and are assigned to this unit when accepted.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={addStaff} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="staff-emails">{capabilities.showAssignedStaff ? 'Staff emails' : 'Teacher Emails'}</Label>
                        <textarea
                          id="staff-emails"
                          className="h-48 max-h-[40vh] w-full resize-none overflow-y-auto rounded-md border bg-background px-3 py-2"
                          placeholder={'teacher1@example.com\nteacher2@example.com'}
                          value={staffEmails}
                          onChange={(event) => setStaffEmails(event.target.value)}
                          required
                        />
                      </div>
                      {addStaffResult && (
                        <div className="space-y-2">
                          {addStaffResult.added.length > 0 && <Alert><AlertDescription>✓ Added {addStaffResult.added.length} {capabilities.showAssignedStaff ? 'staff member(s)' : 'teacher(s)'}</AlertDescription></Alert>}
                          {addStaffResult.pending.length > 0 && <Alert><AlertDescription>✓ Invited {addStaffResult.pending.length}: {addStaffResult.pending.join(', ')}</AlertDescription></Alert>}
                          {addStaffResult.alreadyPending.length > 0 && <Alert><AlertDescription>⚠ Already invited: {addStaffResult.alreadyPending.join(', ')}</AlertDescription></Alert>}
                          {addStaffResult.alreadyAssigned.length > 0 && <Alert><AlertDescription>⚠ Already assigned: {addStaffResult.alreadyAssigned.join(', ')}</AlertDescription></Alert>}
                          {addStaffResult.notTeacher.length > 0 && <Alert variant="destructive"><AlertDescription>✗ Not eligible: {addStaffResult.notTeacher.join(', ')}</AlertDescription></Alert>}
                          {addStaffResult.notFound.length > 0 && <Alert variant="destructive"><AlertDescription>✗ Could not invite: {addStaffResult.notFound.join(', ')}</AlertDescription></Alert>}
                        </div>
                      )}
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={closeAddStaff}>Cancel</Button>
                        <Button type="submit" disabled={addingStaff}>
                          {addingStaff && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {capabilities.showAssignedStaff ? 'Add staff' : 'Add Teachers'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {assignedTeachers.length === 0 && pendingTeachers.length === 0 ? (
              <div className="py-8 text-center">
                <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">{capabilities.showAssignedStaff ? 'No staff assigned' : 'No teachers assigned'}</h3>
                <p className="mb-4 text-muted-foreground">{capabilities.showAssignedStaff ? 'Add staff to collaborate on this unit' : 'Add teachers to collaborate on this unit'}</p>
              </div>
            ) : (
              <>
                {assignedTeachers.length > 0 && (
                  <div className="divide-y">
                    {assignedTeachers.map((teacher) => (
                      <div key={teacher.id} className="flex items-center justify-between gap-3 py-4">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                            <span className="text-sm font-semibold text-[hsl(var(--color-primary-text))]">{teacher.firstName?.charAt(0).toUpperCase() || teacher.email.charAt(0).toUpperCase()}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium">{[teacher.firstName, teacher.lastName].filter(Boolean).join(' ') || teacher.email}</p>
                            <p className="truncate text-sm text-muted-foreground">{teacher.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">{teacher.role === 'teacher' ? 'Instructor' : teacher.role}</Badge>
                          {capabilities.canManageAssignedStaff && currentUser?.id !== teacher.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Remove ${teacher.email}`}
                              title={`Remove ${teacher.email}`}
                              onClick={() => setStaffToRemove(teacher.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {pendingTeachers.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">Pending invitations</h3>
                    <div className="divide-y">
                      {pendingTeachers.map((teacher) => (
                        <div key={teacher.id} className="flex items-center justify-between gap-3 py-4">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{teacher.email}</p>
                            <p className="text-sm text-muted-foreground">Awaiting instructor signup</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Pending</Badge>
                            {capabilities.canManageAssignedStaff && api.resendPendingUnitTeacherInvitation && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Resend invite to ${teacher.email}`}
                                title={`Resend invite to ${teacher.email}`}
                                disabled={resendingPendingId === teacher.id}
                                onClick={() => void resendPending(teacher.id)}
                              >
                                {resendingPendingId === teacher.id
                                  ? <Loader2 className="h-4 w-4 animate-spin" />
                                  : <Mail className="h-4 w-4" />}
                              </Button>
                            )}
                            {capabilities.canManageAssignedStaff && api.removePendingUnitTeacher && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Remove invite for ${teacher.email}`}
                                title={`Remove invite for ${teacher.email}`}
                                onClick={() => setPendingToRemove(teacher.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {capabilities.canPermanentlyDelete && (
        <Card className="border-destructive/40">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-lg">Delete unit</CardTitle>
              <CardDescription>Permanently remove this unit and its associated data.</CardDescription>
            </div>
            <Button variant="destructive" className="self-start sm:self-auto" onClick={openDelete}>
              <Trash2 className="mr-2 h-4 w-4" />Delete unit
            </Button>
          </CardHeader>
        </Card>
      )}

      <Dialog open={staffToRemove !== null} onOpenChange={(open) => !open && setStaffToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{capabilities.showAssignedStaff ? 'Remove staff member' : 'Remove Teacher'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this {capabilities.showAssignedStaff ? 'staff member' : 'teacher'} from the unit?
              They will no longer be able to manage this unit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setStaffToRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void removeStaff()}>{capabilities.showAssignedStaff ? 'Remove staff member' : 'Remove Teacher'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingToRemove !== null} onOpenChange={(open) => !open && setPendingToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove invitation</DialogTitle>
            <DialogDescription>
              Remove this pending tutor invitation from the unit. Their organization instructor invite is not deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setPendingToRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void removePending()}>Remove invitation</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => {
        if (deleting) return;
        if (!open) {
          setDeleteTarget(null);
          setDeleteConfirmation('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.code}?</DialogTitle>
            <DialogDescription>
              This permanently deletes <strong>{deleteTarget?.name}</strong>
              {deleteTarget && ` (${deleteTarget.year}, ${getSemesterLabel(deleteTarget.semester)})`},
              including its enrollments, unit game links, activities and responses, and unit-scoped achievements and statistics.
              Games, user accounts, assignments, and play history remain.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="delete-unit-confirmation">Type <span className="font-semibold text-foreground">{deleteTarget?.code}</span> to confirm</Label>
            <Input id="delete-unit-confirmation" value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} disabled={deleting} autoComplete="off" />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" disabled={deleting} onClick={() => { setDeleteTarget(null); setDeleteConfirmation(''); }}>Cancel</Button>
            <Button variant="destructive" disabled={deleting || !deleteTarget || deleteConfirmation !== deleteTarget.code} onClick={() => void deleteUnit()}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{deleting ? 'Deleting...' : 'Delete unit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
